import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Database, DownloadSimple, UploadSimple, HardDrives, Warning, CheckCircle,
  Key, Plus, Copy, ArrowClockwise, Trash, XCircle, Info,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ManifestDiffCard } from './settings/ManifestDiffCard';
import { BackupHealthWidget } from './settings/BackupHealthWidget';
import { formatDateTime } from '@/utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EXPIRY_DAYS_OPTIONS = [30, 90, 365];

const STATUS_STYLES = {
  active: { label: '有効', klass: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  revoked: { label: '失効', klass: 'bg-zinc-100 text-zinc-500 border-zinc-200 line-through' },
  expired: { label: '期限切れ', klass: 'bg-rose-50 text-rose-700 border-rose-200' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return formatDateTime(iso);
  } catch { return iso; }
};

const daysUntil = (iso) => {
  if (!iso) return null;
  try {
    const ms = new Date(iso).getTime() - Date.now();
    return Math.ceil(ms / (24 * 3600 * 1000));
  } catch { return null; }
};

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success('コピーしました');
  } catch {
    // Fallback for Safari without clipboard-write
    const ta = document.createElement('textarea');
    ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    try { document.execCommand('copy'); toast.success('コピーしました'); }
    catch { toast.error('コピーに失敗しました'); }
    finally { ta.remove(); }
  }
};

const NasStep = ({ num, title, children, muted }) => (
  <div className={`rounded-md border ${muted ? 'border-zinc-200 bg-zinc-50/40' : 'border-zinc-200'} overflow-hidden`}>
    <div className="px-3 py-2 border-b border-zinc-200 flex items-center gap-2 font-semibold text-zinc-800 text-[12px]">
      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${muted ? 'bg-zinc-400' : 'bg-teal-700'} text-white text-[11px] font-bold shrink-0`}>
        {num}
      </span>
      {title}
    </div>
    <div className="px-3 py-3">{children}</div>
  </div>
);

const NasCopy = ({ code, testid }) => (
  <div className="flex gap-2 items-start min-w-0">
    <pre
      className="flex-1 min-w-0 font-mono text-[11px] leading-relaxed px-3 py-2 rounded-md border border-zinc-800 bg-zinc-900 text-zinc-100 overflow-x-auto whitespace-pre-wrap break-all"
      data-testid={testid}
    >{code}</pre>
    <Button
      onClick={() => copyText(code)}
      variant="outline"
      size="sm"
      className="shrink-0 h-auto py-2"
      data-testid={testid ? `${testid}-copy` : undefined}
      title="コピー"
    >
      <Copy size={14} />
    </Button>
  </div>
);

export default function BackupSettings() {
  const [summary, setSummary] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [file, setFile] = useState(null);
  const [agree, setAgree] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  // ── Tokens ────────────────────────────────────────────────────
  const [tokens, setTokens] = useState([]);
  const [tokensLoading, setTokensLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createLabel, setCreateLabel] = useState('');
  const [createExpiry, setCreateExpiry] = useState(90);
  const [creating, setCreating] = useState(false);
  const [issuedToken, setIssuedToken] = useState(null); // { raw_token, token }
  const [nasPath, setNasPath] = useState('/volume1/Web/Sensbey Web App/backups/sensbey-backup-$(date +%Y%m%d).json');

  const loadSummary = async () => {
    try { const r = await axios.get(`${API}/settings/backup/summary`); setSummary(r.data); }
    catch (e) { console.error('load backup summary', e); }
  };

  const loadTokens = async () => {
    setTokensLoading(true);
    try {
      const r = await axios.get(`${API}/settings/backup/tokens`);
      setTokens(r.data?.tokens || []);
    } catch (e) {
      console.error('load backup tokens', e);
      toast.error('バックアップトークンの取得に失敗しました');
    } finally { setTokensLoading(false); }
  };

  useEffect(() => { loadSummary(); loadTokens(); }, []);

  const doExport = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API}/settings/backup/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      const cd = r.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `sensbey-backup-${Date.now()}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('バックアップをダウンロードしました');
    } catch (e) {
      console.error('export failed', e);
      toast.error('エクスポートに失敗しました');
    } finally { setExporting(false); }
  };

  const doImport = async () => {
    if (!file || !agree) return;
    setImporting(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/settings/backup/import`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(r.data);
      toast.success(`インポート完了：${r.data.total_affected} 件を反映しました`);
      setFile(null); setAgree(false);
      if (fileRef.current) fileRef.current.value = '';
      loadSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'インポートに失敗しました');
    } finally { setImporting(false); }
  };

  const openCreate = () => {
    setCreateLabel('');
    setCreateExpiry(90);
    setCreateOpen(true);
  };

  const doCreateToken = async () => {
    if (!createLabel.trim()) { toast.error('ラベルを入力してください'); return; }
    setCreating(true);
    try {
      const r = await axios.post(`${API}/settings/backup/tokens`, {
        label: createLabel.trim(),
        expiry_days: Number(createExpiry),
      });
      setIssuedToken(r.data);
      setCreateOpen(false);
      loadTokens();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'トークンの発行に失敗しました');
    } finally { setCreating(false); }
  };

  const doRevoke = async (t) => {
    if (!window.confirm(`トークン「${t.label}」を失効させますか？\nNAS 側のスケジュールが 401 エラーになります。`)) return;
    try {
      await axios.post(`${API}/settings/backup/tokens/${t.id}/revoke`);
      toast.success(`「${t.label}」を失効させました`);
      loadTokens();
    } catch (e) {
      toast.error(e.response?.data?.detail || '失効に失敗しました');
    }
  };

  const doDelete = async (t) => {
    if (!window.confirm(`トークン「${t.label}」を完全に削除しますか？\nこの操作は取り消せません。`)) return;
    try {
      await axios.delete(`${API}/settings/backup/tokens/${t.id}`);
      toast.success(`「${t.label}」を削除しました`);
      loadTokens();
    } catch (e) {
      toast.error(e.response?.data?.detail || '削除に失敗しました');
    }
  };

  const rawToken = issuedToken?.raw_token || '';
  const pathArg = nasPath.includes(' ') || nasPath.includes('"') ? `"${nasPath.replace(/"/g, '\\"')}"` : nasPath;
  const curlLine = rawToken
    ? `curl -H "Authorization: Bearer ${rawToken}" \\
     -o ${pathArg} \\
     "${process.env.REACT_APP_BACKEND_URL}/api/settings/backup/export"`
    : '';

  return (
    <div className="space-y-6 max-w-4xl" data-testid="backup-settings-page">
      <header>
        <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-zinc-400">設定</p>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900 mt-1 flex items-center gap-2">
          <Database size={26} weight="duotone" className="text-teal-700" />
          バックアップ / 復元
        </h1>
        <p className="text-xs sm:text-sm text-zinc-500 mt-1 leading-relaxed">
          全データを 1 つの JSON ファイルにエクスポート、または同じファイルから復元します。将来追加されるアプリやテーブルも自動的に含まれます。
        </p>
      </header>

      {/* Health widget — at-a-glance status: did the nightly cron run? */}
      <BackupHealthWidget />

      {/* Export */}
      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid="backup-export-card">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-zinc-50 text-teal-700 shrink-0">
            <DownloadSimple size={20} weight="duotone" />
          </span>
          <div>
            <h2 className="font-bold text-zinc-900">エクスポート（バックアップ作成）</h2>
            <p className="text-[12px] text-zinc-500">全コレクションを 1 ファイルにまとめてダウンロードします。</p>
          </div>
        </div>
        <div className="px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-[13px] text-zinc-600" data-testid="backup-summary">
            {summary ? (
              <span>現在のデータ：<b className="text-zinc-900">{summary.collections}</b> テーブル / <b className="text-zinc-900">{summary.total_documents.toLocaleString()}</b> 件のレコード</span>
            ) : <span className="text-zinc-400">集計中…</span>}
          </div>
          <Button onClick={doExport} disabled={exporting} data-testid="backup-export-btn" className="bg-teal-700 hover:bg-teal-800">
            <DownloadSimple size={16} className="mr-1.5" />
            {exporting ? '作成中…' : 'バックアップをダウンロード'}
          </Button>
        </div>
        {summary && summary.counts && (
          <details className="border-t border-zinc-200" data-testid="backup-collections-details">
            <summary className="cursor-pointer px-5 py-2.5 bg-zinc-50/60 hover:bg-zinc-50 text-[12px] font-semibold text-zinc-700 select-none flex items-center gap-2">
              <Info size={13} weight="duotone" />
              含まれるコレクション（{summary.collections} 個）を確認
            </summary>
            <div className="px-5 py-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-0.5 text-[11px] max-h-72 overflow-y-auto" data-testid="backup-collections-list">
              {Object.entries(summary.counts)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([name, count]) => (
                  <div key={name} className="flex justify-between border-b border-zinc-200 py-0.5 gap-2">
                    <span className="font-mono text-zinc-600 truncate" title={name}>{name}</span>
                    <span className="tabular-nums text-zinc-500 shrink-0">{count.toLocaleString()}</span>
                  </div>
                ))}
            </div>
          </details>
        )}
      </div>

      {/* Import */}
      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid="backup-import-card">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-zinc-50 text-emerald-700 shrink-0">
            <UploadSimple size={20} weight="duotone" />
          </span>
          <div>
            <h2 className="font-bold text-zinc-900">インポート（復元 / マージ）</h2>
            <p className="text-[12px] text-zinc-500">同じ ID のデータは上書き、無いデータは追加されます（既存データの削除は行いません）。</p>
          </div>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2.5 text-[12px] text-amber-800 flex gap-2 items-start">
            <Warning size={16} weight="duotone" className="shrink-0 mt-0.5" />
            <span>復元は既存データを上書きする場合があります。念のため、先に現在のデータをエクスポートしてから実行してください。</span>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            data-testid="backup-file-input"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
            className="block w-full text-sm text-zinc-600 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-zinc-100 file:text-zinc-700 hover:file:bg-zinc-200 cursor-pointer"
          />

          <label className="flex items-center gap-2 text-[13px] text-zinc-700 cursor-pointer">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="backup-agree-checkbox" />
            同じ ID のデータが上書きされることを理解しました。
          </label>

          <Button
            onClick={doImport}
            disabled={!file || !agree || importing}
            data-testid="backup-import-btn"
            className="bg-teal-700 hover:bg-teal-800 disabled:opacity-50"
          >
            <UploadSimple size={16} className="mr-1.5" />
            {importing ? '取り込み中…' : 'このファイルから復元'}
          </Button>

          {result && (
            <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-3 text-[12px] text-emerald-900" data-testid="backup-import-result">
              <p className="font-semibold flex items-center gap-1.5"><CheckCircle size={15} weight="duotone" /> インポート完了：{result.collections_processed} テーブル / {result.total_affected} 件を反映</p>
              <div className="mt-2 max-h-48 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5">
                {Object.entries(result.detail || {}).map(([name, d]) => (
                  <div key={name} className="flex justify-between gap-2 border-b border-emerald-100/60 py-0.5">
                    <span className="font-mono truncate">{name}</span>
                    <span className="shrink-0 text-emerald-700">+{d.inserted ?? d.affected ?? 0} / ~{d.updated ?? 0}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Manifest diff — compare two backups side-by-side */}
      <ManifestDiffCard />

      {/* NAS auto-backup tokens */}
      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid="backup-tokens-card">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-zinc-50 text-amber-700 shrink-0">
            <Key size={20} weight="duotone" />
          </span>
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-zinc-900">NAS 自動取得トークン</h2>
            <p className="text-[12px] text-zinc-500">Asustor NAS 等のスケジュールタスクからログイン無しでエクスポートを取得するための長期トークンを発行します。</p>
          </div>
          <Button onClick={openCreate} data-testid="backup-token-create-btn" className="bg-amber-600 hover:bg-amber-700 shrink-0">
            <Plus size={16} className="mr-1.5" />
            トークンを発行
          </Button>
        </div>

        <div className="px-5 py-4">
          {tokensLoading ? (
            <p className="text-[13px] text-zinc-400" data-testid="backup-tokens-loading">読み込み中…</p>
          ) : tokens.length === 0 ? (
            <div className="rounded-md bg-zinc-50 border border-zinc-200 px-3 py-4 text-[13px] text-zinc-500 flex items-start gap-2" data-testid="backup-tokens-empty">
              <Info size={16} weight="duotone" className="shrink-0 mt-0.5" />
              <span>まだトークンはありません。「トークンを発行」から新しいバックアップ用トークンを作成してください。</span>
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="backup-tokens-list">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-zinc-500 border-b border-zinc-200">
                    <th className="py-2 pr-3 font-semibold">ラベル</th>
                    <th className="py-2 pr-3 font-semibold">状態</th>
                    <th className="py-2 pr-3 font-semibold">トークン</th>
                    <th className="py-2 pr-3 font-semibold">期限</th>
                    <th className="py-2 pr-3 font-semibold">最終使用</th>
                    <th className="py-2 pr-3 font-semibold text-right">使用回数</th>
                    <th className="py-2 pl-3 font-semibold w-0"> </th>
                  </tr>
                </thead>
                <tbody>
                  {tokens.map((t) => {
                    const st = STATUS_STYLES[t.status] || STATUS_STYLES.active;
                    const d2exp = daysUntil(t.expires_at);
                    const expWarn = d2exp !== null && d2exp <= 7 && t.status === 'active';
                    return (
                      <tr key={t.id} className="border-b border-zinc-200 last:border-b-0" data-testid={`backup-token-row-${t.id}`}>
                        <td className="py-2.5 pr-3">
                          <div className="font-semibold text-zinc-900">{t.label || '—'}</div>
                          <div className="text-[11px] text-zinc-400">
                            {t.created_by_name || t.created_by_email || '—'} · {fmtDate(t.created_at)}
                          </div>
                        </td>
                        <td className="py-2.5 pr-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${st.klass}`} data-testid={`backup-token-status-${t.id}`}>
                            {st.label}
                          </span>
                        </td>
                        <td className="py-2.5 pr-3 font-mono text-[11px] text-zinc-500">{t.token_hint}</td>
                        <td className={`py-2.5 pr-3 ${expWarn ? 'text-rose-600 font-semibold' : 'text-zinc-600'}`}>
                          {fmtDate(t.expires_at)}
                          {expWarn && <div className="text-[10px] font-normal">残り {d2exp} 日</div>}
                        </td>
                        <td className="py-2.5 pr-3 text-zinc-600">
                          {t.last_used_at ? (
                            <>
                              <div>{fmtDate(t.last_used_at)}</div>
                              <div className="text-[10px] text-zinc-400">{t.last_used_ip || '—'}</div>
                            </>
                          ) : <span className="text-zinc-400">未使用</span>}
                        </td>
                        <td className="py-2.5 pr-3 text-right tabular-nums text-zinc-700">{t.usage_count}</td>
                        <td className="py-2.5 pl-3">
                          <div className="flex gap-1 justify-end">
                            {t.status === 'active' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => doRevoke(t)}
                                data-testid={`backup-token-revoke-${t.id}`}
                                className="h-8 px-2 text-zinc-600 hover:bg-amber-50 hover:text-amber-700"
                                title="失効"
                              >
                                <XCircle size={14} />
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => doDelete(t)}
                              data-testid={`backup-token-delete-${t.id}`}
                              className="h-8 px-2 text-zinc-600 hover:bg-rose-50 hover:text-rose-700"
                              title="削除"
                            >
                              <Trash size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* NAS setup guide */}
      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid="backup-nas-card">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-zinc-50 text-zinc-600 shrink-0">
            <HardDrives size={20} weight="duotone" />
          </span>
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-zinc-900">Asustor NAS 自動バックアップ セットアップガイド</h2>
            <p className="text-[12px] text-zinc-500">初回のみ 6 ステップ。SSH で NAS に入り cron 登録するだけです。</p>
          </div>
        </div>
        <div className="px-5 py-4 text-[13px] text-zinc-700 leading-relaxed space-y-4">
          <p className="text-[12px] text-zinc-600">本アプリはクラウドで動作しているため、NAS 側のスケジュールタスクで <b>毎晩バックアップを取りに来る</b>方式を推奨します。以下は Asustor（ADM）を想定していますが、Synology / QNAP / Linux サーバーでも同じ手順で使えます。</p>

          <NasStep num={1} title="事前準備 — SSH & トークン発行">
            <ul className="list-disc pl-5 space-y-1 text-[12px]">
              <li><b>NAS 側:</b> ADM →「設定」→「ネットワーク & 端末」→「端末」タブ → SSH を有効化（ポート 22）</li>
              <li><b>本アプリ側:</b> 上の「NAS 自動取得トークン」カードで <b>トークンを発行</b> → 表示された生の値を必ずコピー（発行時のみ表示）</li>
            </ul>
          </NasStep>

          <NasStep num={2} title="SSH で NAS に接続">
            <p className="text-[12px] mb-2">Windows は PowerShell / コマンドプロンプト、Mac は Terminal から:</p>
            <NasCopy code="ssh admin@<NASのIP>" testid="nas-guide-step2-cmd" />
            <p className="text-[11px] text-zinc-500 mt-1.5">例: <code className="bg-zinc-100 px-1 rounded">ssh Sensbey-admin@192.168.0.205</code>。パスワードは NAS 管理者パスワード。</p>
          </NasStep>

          <NasStep num={3} title="バックアップ保存先フォルダーを作成">
            <NasCopy code={`mkdir -p "/volume1/Web/Sensbey Web App/backups"`} testid="nas-guide-step3-cmd" />
            <p className="text-[11px] text-zinc-500 mt-1.5">パスは自由に変更可能。<b>半角スペースを含む場合は必ずクォートで囲みます</b>（この後の全コマンドで <code className="bg-zinc-100 px-1 rounded">&quot;…&quot;</code> を使っています）。</p>
          </NasStep>

          <NasStep num={4} title="手動で 1 回テスト（動作確認）">
            <p className="text-[12px] mb-2"><b className="text-rose-600">&lt;トークン&gt;</b> の部分を、上で発行した実際のトークンに置き換えて 1 行で実行:</p>
            <NasCopy
              code={`curl -H "Authorization: Bearer <トークン>" -o "/volume1/Web/Sensbey Web App/backups/sensbey-backup-$(date +%Y%m%d).json" "${process.env.REACT_APP_BACKEND_URL}/api/settings/backup/export"`}
              testid="nas-guide-step4-cmd"
            />
            <p className="text-[12px] mt-2">ダウンロード進捗が表示され、13 MB 前後のファイルが作られていれば成功:</p>
            <NasCopy code={`ls -lh "/volume1/Web/Sensbey Web App/backups/"`} testid="nas-guide-step4-ls" />
          </NasStep>

          <NasStep num={5} title="毎晩 02:00 に自動実行を登録（cron）">
            <p className="text-[12px] mb-2">3 コマンドで登録。<b className="text-rose-600">&lt;トークン&gt;</b> を実際の値に置き換えてください。<code className="bg-zinc-100 px-1 rounded">\%</code> のバックスラッシュは cron 用のエスケープなので<b>そのまま残します</b>。</p>
            <div className="space-y-2">
              <NasCopy code="sudo crontab -l > /tmp/mycron" testid="nas-guide-step5-a" />
              <NasCopy
                code={`echo '0 2 * * * /usr/bin/curl -H "Authorization: Bearer <トークン>" -o "/volume1/Web/Sensbey Web App/backups/sensbey-backup-$(date +\\%Y\\%m\\%d).json" "${process.env.REACT_APP_BACKEND_URL}/api/settings/backup/export"' >> /tmp/mycron`}
                testid="nas-guide-step5-b"
              />
              <NasCopy code="sudo crontab /tmp/mycron && sudo crontab -l | tail -3" testid="nas-guide-step5-c" />
            </div>
            <p className="text-[11px] text-zinc-500 mt-1.5">最後のコマンドの出力の末尾行が <code className="bg-zinc-100 px-1 rounded">0 2 * * *</code> で始まっていれば登録完了。</p>
          </NasStep>

          <NasStep num={6} title="翌朝、動作を確認">
            <p className="text-[12px] mb-2"><b>NAS 側:</b> 新しい日付のファイルが増えているか確認</p>
            <NasCopy code={`ls -lh "/volume1/Web/Sensbey Web App/backups/"`} testid="nas-guide-step6-cmd" />
            <p className="text-[12px] mt-2"><b>本アプリ側:</b> 上の「NAS 自動取得トークン」テーブルで <b>最終使用</b> の日時と <b>使用回数</b> が更新されていれば OK。</p>
          </NasStep>

          <NasStep num="+" title="任意 · 30 日を超えた古いバックアップを自動削除" muted>
            <p className="text-[12px] mb-2">ディスク圧迫を防ぐため、Step 5 と同じ方法でもう 1 行追加できます:</p>
            <NasCopy
              code={`0 3 * * * find "/volume1/Web/Sensbey Web App/backups" -name 'sensbey-backup-*.json' -mtime +30 -delete`}
              testid="nas-guide-optional-retention"
            />
          </NasStep>

          <details className="rounded-md border border-zinc-200 group" data-testid="nas-guide-troubleshooting">
            <summary className="cursor-pointer px-3 py-2 bg-zinc-50 border-b border-zinc-200 border-b-0 group-open:border-b font-semibold text-zinc-800 text-[12px] flex items-center gap-2 select-none">
              <Info size={14} weight="duotone" />
              よくあるトラブル
            </summary>
            <div className="px-3 py-3 text-[12px] space-y-2 text-zinc-700">
              <div><b>curl が「Could not resolve host: Web」のようなエラー</b><br />→ 保存先パスに半角スペースが含まれているのにクォートで囲まれていないと、シェルがスペースで単語を区切ってしまいます。上のコマンドのように <b>パス全体を <code className="bg-zinc-100 px-1 rounded">&quot;…&quot;</code> で囲む</b>ことで解決します。</div>
              <div><b>401 Unauthorized が返る</b><br />→ トークンが失効／期限切れ、またはコピー漏れ。上のトークンテーブルで状態を確認し、必要なら再発行してください。</div>
              <div><b>「Task Scheduler」/「タスクスケジューラー」が ADM に無い</b><br />→ 一部の Asustor モデルには GUI スケジューラーが無いため、上の Step 5（cron を直接登録）で対応します。GUI は不要です。</div>
              <div><b>vi の使い方が分からない</b><br />→ 上の Step 5 の方法（<code>crontab -l</code> をファイルに保存 → <code>echo</code> で追記 → <code>crontab &lt;ファイル&gt;</code>）を使えば vi を開く必要はありません。</div>
              <div><b>翌朝ファイルが増えていない</b><br />→ <code>sudo crontab -l</code> で登録内容を確認、<code>sudo killall -HUP crond</code> で cron 再読み込み。それでも動かない場合は本アプリ側で <b>最終使用</b> が更新されていないことを確認し、トークンの有効期限をチェック。</div>
            </div>
          </details>

          <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-800 flex items-start gap-2">
            <Warning size={16} weight="duotone" className="shrink-0 mt-0.5" />
            <span>
              トークンは <b>発行時に一度だけ</b>表示されます。忘れた場合は該当トークンを削除して新しいトークンを発行してください。<br />
              期限が近づいたら（テーブルに「残り 7 日」と表示）新しいトークンを発行 → Step 5 の方法で cron 内容を差し替え → 古いトークンを失効 の順番で更新してください。
            </span>
          </div>
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="backup-token-create-dialog">
          <DialogHeader>
            <DialogTitle>バックアップトークンを発行</DialogTitle>
            <DialogDescription>NAS のスケジュールタスクから利用する長期トークンです。用途がわかるラベル（例：Asustor NAS 夜間バックアップ）を付けてください。</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-[13px] font-semibold text-zinc-700">ラベル</label>
              <Input
                value={createLabel}
                onChange={(e) => setCreateLabel(e.target.value)}
                placeholder="例：Asustor NAS 夜間バックアップ"
                data-testid="backup-token-label-input"
                maxLength={80}
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[13px] font-semibold text-zinc-700">有効期間</label>
              <div className="grid grid-cols-3 gap-2" data-testid="backup-token-expiry-picker">
                {EXPIRY_DAYS_OPTIONS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setCreateExpiry(d)}
                    data-testid={`backup-token-expiry-${d}`}
                    className={`rounded-md border px-3 py-2 text-[13px] font-semibold transition-colors ${
                      createExpiry === d
                        ? 'bg-amber-50 border-amber-500 text-amber-700 ring-2 ring-amber-200'
                        : 'bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300'
                    }`}
                  >
                    {d} 日
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-zinc-400">期限が近づいたら新しいトークンを発行し、NAS 側の設定を差し替えてください。</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>キャンセル</Button>
            <Button
              onClick={doCreateToken}
              disabled={creating || !createLabel.trim()}
              data-testid="backup-token-create-confirm"
              className="bg-amber-600 hover:bg-amber-700"
            >
              {creating ? '発行中…' : '発行する'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Once-only token reveal dialog */}
      <Dialog open={!!issuedToken} onOpenChange={(o) => !o && setIssuedToken(null)}>
        <DialogContent data-testid="backup-token-reveal-dialog" className="max-w-2xl overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle size={22} weight="duotone" className="text-emerald-600" />
              トークンを発行しました
            </DialogTitle>
            <DialogDescription>
              下記の値は<b className="text-rose-600">この画面を閉じると二度と表示されません</b>。今すぐコピーして NAS 側に設定してください。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 min-w-0">
            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-800 flex items-start gap-2">
              <Warning size={16} weight="duotone" className="shrink-0 mt-0.5" />
              <span>トークンは Sensbey Web App の完全なバックアップを取得できます。第三者に渡さないでください。</span>
            </div>

            <div className="space-y-1.5">
              <label className="text-[13px] font-semibold text-zinc-700">トークン（ラベル：{issuedToken?.token?.label}）</label>
              <div className="flex gap-2 items-center min-w-0">
                <input
                  readOnly
                  value={rawToken}
                  data-testid="backup-token-reveal-value"
                  className="flex-1 min-w-0 font-mono text-[12px] px-3 py-2 rounded-md border border-zinc-300 bg-zinc-50 select-all"
                  onFocus={(e) => e.target.select()}
                />
                <Button
                  onClick={() => copyText(rawToken)}
                  data-testid="backup-token-reveal-copy"
                  className="bg-zinc-800 hover:bg-zinc-900 shrink-0"
                >
                  <Copy size={14} className="mr-1" />
                  コピー
                </Button>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[13px] font-semibold text-zinc-700">NAS 保存先パス</label>
              <input
                value={nasPath}
                onChange={(e) => setNasPath(e.target.value)}
                data-testid="backup-token-reveal-path"
                spellCheck={false}
                className="w-full font-mono text-[12px] px-3 py-2 rounded-md border border-zinc-300 bg-white"
              />
              <p className="text-[11px] text-zinc-400">下の curl コマンドに反映されます。スペースを含むパスは自動でクォートされます。</p>
            </div>

            <div className="space-y-1.5">
              <label className="text-[13px] font-semibold text-zinc-700">NAS 用 curl コマンド</label>
              <div className="flex gap-2 items-start min-w-0">
                <pre className="flex-1 min-w-0 font-mono text-[11px] leading-relaxed px-3 py-2 rounded-md border border-zinc-300 bg-zinc-50 overflow-x-auto whitespace-pre" data-testid="backup-token-reveal-curl">{curlLine}</pre>
                <Button
                  onClick={() => copyText(curlLine)}
                  data-testid="backup-token-reveal-curl-copy"
                  variant="outline"
                  className="shrink-0"
                >
                  <Copy size={14} />
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setIssuedToken(null)}
              data-testid="backup-token-reveal-close"
              className="bg-teal-700 hover:bg-teal-800"
            >
              <ArrowClockwise size={14} className="mr-1.5" />
              コピーしました、閉じる
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
